import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";

import { PageContainer, PageEmptyState, PageHeader } from "../../components/AssetBreadCrumb";
import { SkeletonGroup } from "../../components/Skeleton";
import ModelsToolbar from "../../components/Models/ModelsToolbar";
import TemplateCard, { TemplateCardSkeleton } from "../../components/Models/TemplateCard";
import { ModelGardenItem } from "../../components/Modal/ModelGardenModal/types";

import { ReactComponent as ErodeIcon } from "../../assets/icons/erode_icon.svg";

import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { useCreatorMode } from "../../hooks/useCreatorMode";

import { fetchModelSchemaTemplates } from "../../store/modelSchemaTemplateSlice";
import { modalViewsConst, setModalState } from "../../store/modalSlice";

import { mapWithConcurrency } from "../../utils/modelsList";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import { getCurrentUserGroups } from "../../utils/ownerScope";
// Author public profiles are resolved client-side, by authorType
import {
  getUserForCard,
  getPublicProfileForCard,
  getOrgPublicProfileForCard,
} from "../../graphql/customQueries";

import type { AppDispatch, RootState } from "../../store";

const client = generateClient();

const SORT_OPTIONS = [
  { value: "recommended", label: "Recommended" },
  { value: "name", label: "Name A–Z" },
  { value: "newest", label: "Newest" },
];

// Splits on whether a template can deploy straight away; requiresTraining defaults false, so null reads as ready
const TRAINING_TABS = [
  { id: "all", label: "All" },
  { id: "ready", label: "Ready to deploy" },
  { id: "training", label: "Needs training" },
];

const matchesTrainingTab = (template: ModelGardenItem, tab: string) =>
  tab === "all" || (tab === "training") === (template.templateData?.requiresTraining === true);

// Model Templates: browse published templates and deploy one. Deployed models live on Data & Models.
const ModelTemplatesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { capture } = usePostHogCapture();

  const modelSchemaTemplateState = useSelector((state: RootState) => state.modelSchemaTemplates);
  const [activeTab, setActiveTab] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("recommended");
  // Null until the session is read. Admins see every template; everyone else only the historical ones, whose
  // creation flow is meant for clients (stage 4, decision 1); forecast creation stays admin-only
  const [cognitoGroups, setCognitoGroups] = useState<string[] | null>(null);
  const isAdmin = Boolean(cognitoGroups?.includes("Admin") || cognitoGroups?.includes("AdminReadOnly"));
  const [userCache, setUserCache] = useState<Record<string, any>>({});
  // Per-mount by design — author name/avatar refreshes the next time the page opens.
  const [authorProfileCache, setAuthorProfileCache] = useState<Record<string, any>>({});
  // Ids currently being fetched — the cache guards below read a closure value, so without these two overlapping effect runs would issue the same queries twice
  const usersInFlight = useRef<Set<string>>(new Set());
  const profilesInFlight = useRef<Set<string>>(new Set());

  const templateList = useMemo(
    () => modelSchemaTemplateState?.templateList || [],
    [modelSchemaTemplateState?.templateList]
  );

  const loadTemplates = useCallback(() => {
    dispatch(fetchModelSchemaTemplates());
  }, [dispatch]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    getCurrentUserGroups().then(setCognitoGroups);
  }, []);

  useEffect(() => {
    capture(POSTHOG_EVENTS.MODEL_GARDEN_PAGE_VIEWED);
    // Fire once on mount — capture identity is not stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve creator User records (same-org only) for templates without a public author profile.
  useEffect(() => {
    const fetchCreatorData = async () => {
      const uniqueCreatorIds = Array.from(
        new Set(templateList.filter((t: any) => t.createdBy).map((t: any) => t.createdBy))
      ) as string[];
      const idsToFetch = uniqueCreatorIds.filter(
        (id) => !(id in userCache) && !usersInFlight.current.has(id)
      );
      if (idsToFetch.length === 0) return;
      idsToFetch.forEach((id) => usersInFlight.current.add(id));

      try {
        const results = await mapWithConcurrency(idsToFetch, (userId) =>
          (client.graphql({ query: getUserForCard, variables: { id: userId } }) as Promise<any>)
            .then(({ data }: any) => ({ userId, user: data?.getUser }))
            .catch((error: unknown) => {
              console.error(`Error fetching user ${userId}:`, error);
              return { userId, user: null };
            })
        );

        // Cache nulls too, so a failed lookup isn't retried on every render.
        setUserCache((prev) => {
          const next = { ...prev };
          results.forEach(({ userId, user }) => {
            next[userId] = user;
          });
          return next;
        });
      } finally {
        // Release even on an unexpected throw, or these ids would never be retried
        idsToFetch.forEach((id) => usersInFlight.current.delete(id));
      }
    };

    if (templateList.length > 0) fetchCreatorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateList]);

  // Resolve the credited author's public profile (USER -> PublicProfile, ORGANIZATION -> OrganizationPublicProfile), keyed by authorProfileId.
  useEffect(() => {
    const fetchAuthorProfiles = async () => {
      const authors = templateList.filter((t: any) => t.authorType && t.authorProfileId);
      const toFetch = Array.from(
        new Map(
          authors
            .filter(
              (t: any) =>
                !(t.authorProfileId in authorProfileCache) &&
                !profilesInFlight.current.has(t.authorProfileId)
            )
            .map((t: any) => [t.authorProfileId, t])
        ).values()
      ) as any[];
      if (toFetch.length === 0) return;
      toFetch.forEach((t) => profilesInFlight.current.add(t.authorProfileId));

      try {
        const results = await mapWithConcurrency(toFetch, (t) =>
          (client.graphql({
            query:
              t.authorType === "ORGANIZATION"
                ? getOrgPublicProfileForCard
                : getPublicProfileForCard,
            variables: { id: t.authorProfileId },
          }) as Promise<any>)
            .then(({ data }: any) => ({
              id: t.authorProfileId,
              profile: data?.getPublicProfile || data?.getOrganizationPublicProfile || null,
            }))
            .catch((error: unknown) => {
              console.error(`Error fetching author profile ${t.authorProfileId}:`, error);
              return { id: t.authorProfileId, profile: null };
            })
        );

        setAuthorProfileCache((prev) => {
          const next = { ...prev };
          results.forEach(({ id, profile }) => {
            next[id] = profile;
          });
          return next;
        });
      } finally {
        // Release even on an unexpected throw, or these ids would never be retried
        toFetch.forEach((t) => profilesInFlight.current.delete(t.authorProfileId));
      }
    };

    if (templateList.length > 0) fetchAuthorProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateList]);

  // Fallback chain: chosen public profile -> createdBy User lookup -> "Erode AI".
  const resolveCreator = useCallback(
    (template: any) => {
      const publicProfile = template.authorProfileId
        ? authorProfileCache[template.authorProfileId]
        : null;

      if (publicProfile) {
        return {
          name: (publicProfile.showDisplayName && publicProfile.displayName) || "Erode AI",
          avatar: undefined,
          profilePictureUrl:
            (publicProfile.showProfilePicture && publicProfile.profilePictureUrl) || undefined,
        };
      }

      const creatorUser = template.createdBy ? userCache[template.createdBy] : null;
      return {
        name: creatorUser?.name || "Erode AI",
        avatar: undefined,
        profilePictureUrl: creatorUser?.profilePictureUrl || undefined,
      };
    },
    [authorProfileCache, userCache]
  );

  const templates: ModelGardenItem[] = useMemo(
    () =>
      templateList
        // Strict check: only explicitly-active templates are selectable — null/missing isActive counts as inactive.
        .filter((template: any) => template.isActive === true)
        // Non-admins see historical templates and external forecast templates (no processor; forecasts are uploaded)
        .filter((template: any) => isAdmin || template.modelKind === "HISTORICAL" || template.external === true)
        .map((template: any) => ({
          id: template.id,
          name: template.name,
          description: template.description,
          type: template.createdBy ? "Internal" : "Community",
          tags: template.tags || [],
          category: template.category,
          version: template.version,
          thumbnail: template.profilePictureUrl || undefined,
          executionUrl: template.executionUrl,
          modelKind: template.modelKind ?? null,
          external: template.external === true,
          creator: resolveCreator(template),
          templateData: template,
        })),
    [templateList, resolveCreator, isAdmin]
  );

  // ModelSchemaTemplate.category is required, so every template lands on exactly one chip.
  const availableCategories = useMemo(() => {
    const categorySet = new Set<string>();
    templates.forEach((t) => t.category && categorySet.add(t.category));
    return Array.from(categorySet).sort();
  }, [templates]);

  const tabTemplates = useMemo(
    () => templates.filter((t) => matchesTrainingTab(t, activeTab)),
    [templates, activeTab]
  );

  const countFor = (tab: string) => templates.filter((t) => matchesTrainingTab(t, tab)).length;

  const visibleTemplates = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    let result = tabTemplates.filter((t) => {
      if (selectedCategories.length && !(t.category && selectedCategories.includes(t.category))) {
        return false;
      }
      if (!q) return true;
      const haystack = [t.name, t.description, t.id, t.category, (t.tags || []).join(" ")]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });

    result = [...result].sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "newest") {
        const at = new Date((a as any).templateData?.createdAt || 0).getTime();
        const bt = new Date((b as any).templateData?.createdAt || 0).getTime();
        return bt - at;
      }
      return 0; // recommended — preserve the backend's order
    });

    return result;
  }, [tabTemplates, searchValue, selectedCategories, sortBy]);

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  // Hand the chosen template to the schema wizard; __backTo null makes its Previous close back to this page, and a successful deploy lands on the Models tab.
  const handleTemplateSelect = (template: ModelGardenItem) => {
    capture(POSTHOG_EVENTS.MODEL_TEMPLATE_SELECTED, {
      template_id: template.id,
      category: template.category,
    });
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.newProcessedModelSchema,
        properties: {
          __modalSize: "md",
          selectedTemplate: (template as any).templateData || template,
          __backTo: null,
          __navigateOnCreate: "/home/data-models?tab=models",
        },
      })
    );
  };

  // cognitoGroups is null only until getCurrentUserGroups answers; it resolves to [] rather than rejecting, so this can't stay true
  const isLoading = (modelSchemaTemplateState?.loading === "pending" && templateList.length === 0) || cognitoGroups === null;
  // A failed fetch would otherwise render as "No templates available" — indistinguishable from an account that genuinely has none.
  const hasLoadError =
    !isLoading && modelSchemaTemplateState?.loading === "failed" && templateList.length === 0;
  const hasTemplates = templates.length > 0;

  // Same segmented control as Custom Views; counts hide below sm and while loading
  // The panel copy is named by its own visible heading — two live groups answering to one aria-label is ambiguous to query
  const trainingSeg = (labelledBy?: string) => (
    <div
      className="eai-seg max-w-full overflow-x-auto"
      role="group"
      aria-label={labelledBy ? undefined : "Template training"}
      aria-labelledby={labelledBy}
    >
      {TRAINING_TABS.map(({ id, label }) => {
        const active = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            disabled={isLoading}
            className={`eai-seg-item whitespace-nowrap ${active ? "eai-seg-item-active" : ""}`}
            aria-current={active ? "true" : undefined}
          >
            {label}
            {!isLoading && <span className="eai-count-pill hidden sm:inline-flex">{countFor(id)}</span>}
          </button>
        );
      })}
    </div>
  );

  return (
    <PageContainer>
      {/* Header on Home's shape — eyebrow and title, no portrait tile: this is a catalogue, not an entity */}
      <PageHeader
        eyebrow={
          <>
            Templates
            {hasTemplates && <span className="eai-count-pill ml-2">{templates.length}</span>}
          </>
        }
        title="Model Templates"
      />

      {/* Rendered during load too, so the chrome is settled before the cards arrive. Sibling of the grid, not nested in the masthead: a sticky element is released the moment its own parent scrolls past. */}
      {(isLoading || hasTemplates) && !hasLoadError && (
        <>
          {/* Desktop only — on a phone the picker moves into the toolbar's Filters panel so the page opens like Home */}
          <div className="mb-5 hidden sm:block">{trainingSeg()}</div>
          <ModelsToolbar
            sticky
            className="eai-gap-toolbar"
            searchId="model-templates-search"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Template name, description or category…"
            availableChips={isLoading ? [] : availableCategories}
            selectedChips={selectedCategories}
            onChipToggle={handleCategoryToggle}
            countLabel={isLoading ? "" : `${visibleTemplates.length} of ${tabTemplates.length} templates`}
            sortBy={sortBy}
            onSortChange={setSortBy}
            sortOptions={SORT_OPTIONS}
            sortAriaLabel="Sort templates"
            disabled={isLoading}
            mobileControls={
              <div>
                <span id="model-templates-training-label" className="eai-meta-label mb-1.5 block">
                  Training
                </span>
                {trainingSeg("model-templates-training-label")}
              </div>
            }
            mobileActiveCount={activeTab === "all" ? 0 : 1}
            collapseCountOnMobile
          />
        </>
      )}

      <div>
        {isLoading ? (
          <SkeletonGroup count={8} label="Loading templates" className="eai-card-grid">
            <TemplateCardSkeleton />
          </SkeletonGroup>
        ) : hasLoadError ? (
          <PageEmptyState
            title="Couldn't load templates"
            description="Something went wrong fetching the model templates. Check your connection and try again."
            action={
              <button onClick={loadTemplates} className="eai-btn-cta" type="button">
                Try again
              </button>
            }
          />
        ) : !hasTemplates ? (
          <PageEmptyState
            icon={<ErodeIcon className="h-6 w-6" />}
            title="No templates available"
            description="Published model templates will appear here once they're available."
          />
        ) : visibleTemplates.length === 0 ? (
          <PageEmptyState
            title="No templates found"
            description={
              tabTemplates.length === 0
                ? `No templates ${activeTab === "training" ? "need training" : "are ready to deploy"} yet.`
                : "Adjust your search or category filters."
            }
          />
        ) : (
          <div className="eai-card-grid">
            {visibleTemplates.map((template) => (
              <TemplateCard key={template.id} template={template} onSelect={handleTemplateSelect} />
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
};

// Open to every user since stage 4 of the historical models (non-admins see historical templates only, above);
// the admin schema manager at /home/model-schemas keeps its gate.
// Templates only exist to create models, so the page is open in creator mode (or to a full Admin); anyone else lands on their data
const ModelTemplatesRoute: React.FC = () => {
  const { canCreate, isResolved } = useCreatorMode();
  // Wait for the groups so an Admin isn't bounced while the session loads
  if (!isResolved) return null;
  if (!canCreate) return <Navigate to="/home/data-models" replace />;
  return <ModelTemplatesPage />;
};

export default ModelTemplatesRoute;
